import axios from 'axios'

/**
 * facecock-was 에러 봉투 (C13).
 * `{ errorStatus, errorCode, errorMsg }` — `errorCode` 는 보통 HTTP 상태값과 같다.
 *
 * 두 표면이 여기에 **필드를 덧붙인 전용 봉투**를 쓴다(공용 타입에 nullable 을 더하면 전 API 응답에
 * `null` 필드가 붙기 때문 — #569·#583 이 같은 이유로 전용 타입을 만들었다):
 *  - 로그인 실패: `+ reason` (#583 §④)
 *  - 기능 차단(409): `+ blockReason·feature` (#584 §3, `#761` §3 — 이용권 갱신 필드 폐지)
 *  - 구장 기본정보 저장 실패(400): `+ fieldErrors[]` (#594 §2)
 *  - 휴무·차단 등록의 예약 충돌(409): `+ conflictReservationCount·confirmedCount·pendingCount·conflictHorizonDays` (#604, 백엔드 #603)
 *  - 예약 변경의 슬롯 충돌(409): `+ conflictReason·conflictReasonLabel·retriable·blockedSlotStartsAt` (#669 §3)
 *  - 예약 기능 활성화 조건 미충족(409): `+ unmetConditions[]` (#758, 백엔드 #757 `VENUE_RESERVATION_ACTIVATION_BLOCKED`)
 */
interface ErrorFieldEntry {
  field?: unknown
  code?: unknown
  message?: unknown
}

interface ErrorEnvelope {
  errorStatus?: string
  errorCode?: number
  errorMsg?: string
  reason?: unknown
  blockReason?: unknown
  feature?: unknown
  fieldErrors?: unknown
  conflictReservationCount?: unknown
  confirmedCount?: unknown
  pendingCount?: unknown
  conflictHorizonDays?: unknown
  conflictReason?: unknown
  conflictReasonLabel?: unknown
  retriable?: unknown
  blockedSlotStartsAt?: unknown
  unmetConditions?: unknown
}

const DEFAULT_MESSAGE = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.'

/**
 * 409 차단 상세 (#584 §3, `#761` §3 개정 — 이용권 폐지로 필드가 5개로 축소됐다).
 *
 * 🚨 `blockReason` 은 **forward-only** 다 — 모르는 값은 *차단*으로 처리하고 서버 `errorMsg` 를
 * 그대로 노출한다. 그래서 유니온이 아니라 `string` 이다(알려진 값: `OPERATION_UNVERIFIED` ·
 * `RESERVATION_DISABLED` · `VENUE_NOT_EXPOSED`).
 * 🚫 **갱신 CTA 를 그리지 않는다** — 결제로 풀리는 차단이 더 이상 없다. 화면은 `errorMsg` 를 그대로 노출한다.
 */
export interface BlockedDetail {
  blockReason: string
  /** 막힌 기능(`VenueManagerFeature.name`). 운영 사정 누출 방지를 위해 사용자 표면에는 보통 `null`. */
  feature: string | null
}

/** `#594` §5 — forward-only. `field` 는 요청 JSON 필드명과 글자까지 같다(입력칸 하이라이트 계약). */
export interface VenueProfileFieldError {
  field: string
  code: string
  message: string
}

/**
 * 휴무·차단 등록이 확정/진행중 예약과 겹쳤을 때의 409 상세 (#604, 백엔드 #603 `VenueClosureConflictErrorResponse`).
 *
 * 🚨 판별 기준은 **`conflictReservationCount` 의 존재**다 — 게이팅 409(`blocked`)와 상태코드만으로는
 * 구분되지 않는다. 값이 있으면 "다시 확인 후 `force:true` 로 강행 등록할지" 를 사용자에게 묻는다.
 */
export interface ConflictDetail {
  count: number
  confirmedCount: number
  pendingCount: number
  horizonDays: number
}

/**
 * 예약 변경(`PATCH .../schedule`)의 슬롯 충돌 409 상세 (#669 §3-1, `VenueRescheduleConflictErrorResponse`).
 *
 * 🚨 판별 기준은 **`conflictReason` 의 존재**다 — 사유 코드 없는 409(상태 불허·동시성 패배 등, #669 §3-2)와
 * 이용권 차단 409(`blocked`)는 이 필드가 없어 자연히 걸러진다.
 * 🚨 `conflictReason`은 forward-only다 — 모르는 코드도 그대로 담고, 문구는 항상 서버 `conflictReasonLabel`/
 * `errorMsg`를 쓴다(코드↔문구 매핑 표를 클라이언트가 따로 두지 않는다).
 *
 * 🔸 **전화·현장 예약 등록(`POST .../reservations`, #675 §2-2)의 슬롯 충돌(409
 * `VENUE_RESERVATION_MANUAL_CONFLICT`)도 이 타입·`readRescheduleConflict`를 그대로 재사용한다** — 두 EP의
 * 409 봉투가 `conflictReason`/`conflictReasonLabel`/`retriable`/`blockedSlotStartsAt`로 문자 그대로 동일하고
 * (api-spec "conflictReason 어휘 — #669 와 동일 값"), 판별 함수도 필드 존재 여부만 보고 엔드포인트를 가리지
 * 않는다. 필드명이 "reschedule"이지만 등록 409도 여기 담긴다 — 리네임하지 않는다(신규 리팩터링 범위 밖).
 */
export interface RescheduleConflictDetail {
  conflictReason: string
  conflictReasonLabel: string
  /** "잠시 후 다시 시도할 가치가 있는가" — `SLOT_HELD` 만 `true`. */
  retriable: boolean
  /** 사전 검사에서 걸린 경우에만 값. 진짜 동시 요청(DB 충돌)에서는 `null`. */
  blockedSlotStartsAt: string | null
}

/**
 * 예약 기능 켜기 시도가 활성화 조건 미충족으로 거부된 409 상세 (#758, 백엔드 #757 §2
 * `VENUE_RESERVATION_ACTIVATION_BLOCKED`).
 *
 * 🚨 판별 기준은 **`unmetConditions` 배열의 존재**다 — 이용권 차단 409(`blocked`)와는 별개 필드라
 * 자연히 갈린다. `condition` 은 조회 EP(`VenueReservationConditionState.condition`)와 같은
 * forward-only 식별자다(#757 §0-②) — 이동 버튼 매핑에 그대로 재사용한다.
 */
export interface VenueReservationUnmetCondition {
  condition: string
  label: string
  description: string
}

export interface ApiErrorOptions {
  /** 로그인 실패 사유 코드 (#583 §④). forward-only. */
  reason?: string
  blocked?: BlockedDetail
  /** 구장 기본정보 저장 필드별 검증 실패 (#594 §2). */
  fieldErrors?: VenueProfileFieldError[]
  /** 휴무·차단 등록의 예약 충돌 상세 (#604). */
  conflict?: ConflictDetail
  /** 예약 변경의 슬롯 충돌 상세 (#669 §3-1). */
  rescheduleConflict?: RescheduleConflictDetail
  /** 예약 기능 활성화 조건 미충족 상세 (#758, 백엔드 #757 §2). */
  reservationUnmetConditions?: VenueReservationUnmetCondition[]
}

export class ApiError extends Error {
  /** HTTP 상태. 네트워크 오류 등 응답이 없으면 0. */
  readonly status: number
  /** 서버가 준 `errorCode`. 없으면 `status` 와 같다. */
  readonly code: number
  /**
   * 로그인 실패 사유 — `ACCOUNT_NOT_FOUND` / `PASSWORD_MISMATCH` / `ACCOUNT_INACTIVE` /
   * `VENUE_NOT_LINKED` / `INVALID_PASSWORD_ENCODING` 등.
   * 🚨 **문구를 여기서 만들지 않는다.** 본문은 서버 `errorMsg` 가 정본이고 이 값은 화면의
   * 포커스·톤·보조 CTA 만 정한다(설계 D3).
   */
  readonly reason?: string
  /** 409 차단 상세. 값이 있으면 "운영 인증 미완료·예약 기능 OFF·비노출 등으로 기능이 막힌 것"이다. */
  readonly blocked?: BlockedDetail
  /** 구장 기본정보 저장 필드별 검증 실패(400). 값이 있으면 폼이 필드별로 하이라이트한다. */
  readonly fieldErrors?: VenueProfileFieldError[]
  /** 휴무·차단 등록의 예약 충돌 상세(409). 값이 있으면 "예약이 겹친다"는 뜻이다(#604). */
  readonly conflict?: ConflictDetail
  /**
   * 예약 변경의 슬롯 충돌 상세(409). 값이 있으면 "이동하려는 시간이 막혀 있다"는 뜻이다(#669).
   * 🔸 전화·현장 예약 등록(#675)의 슬롯 충돌 409도 이 필드에 담긴다 — 두 EP의 봉투가 동일하다.
   */
  readonly rescheduleConflict?: RescheduleConflictDetail
  /**
   * 예약 기능 활성화 조건 미충족 상세(409). 값이 있으면 "켜기 조건이 아직 안 채워졌다"는 뜻이다
   * (#758, 백엔드 #757 §2).
   */
  readonly reservationUnmetConditions?: VenueReservationUnmetCondition[]

  constructor(status: number, code: number, message: string, options?: ApiErrorOptions) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.reason = options?.reason
    this.blocked = options?.blocked
    this.fieldErrors = options?.fieldErrors
    this.conflict = options?.conflict
    this.rescheduleConflict = options?.rescheduleConflict
    this.reservationUnmetConditions = options?.reservationUnmetConditions
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /**
   * 기능 차단(409) — 운영 인증 미완료·예약 기능 OFF·비노출 등(#584 §3, `#761` §3). 쓰기 화면은
   * mutation 에러 핸들러에서 `error.message`(서버 `errorMsg`)를 그대로 노출한다 — 갱신 CTA는 그리지 않는다.
   */
  get isConflict(): boolean {
    return this.status === 409
  }

  /**
   * 예약 변경(#669)·전화·현장 예약 등록(#675) 공통 슬롯 충돌(409). 값이 있으면 "그 시간은
   * 쓸 수 없다"는 뜻이다(#669 §3-1).
   */
  get isRescheduleConflict(): boolean {
    return this.rescheduleConflict !== undefined
  }

  /** 예약 기능 켜기 조건 미충족(409, #758). 값이 있으면 "조건을 채우고 다시 오라"는 뜻이다(결제가 답이 아니다). */
  get isReservationActivationBlocked(): boolean {
    return this.reservationUnmetConditions !== undefined
  }
}

/** 세션 자체가 끊긴 경우 — refresh 호출이 실패했을 때만 만든다(설계 불변식 5). */
export class SessionExpiredError extends Error {
  constructor(message = '로그인이 만료되었습니다. 다시 로그인해 주세요.') {
    super(message)
    this.name = 'SessionExpiredError'
  }
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined
}

/**
 * 409 전용 봉투를 읽는다.
 * 🚨 판별 기준은 **`blockReason` 의 존재**다. 상태코드만으로 판단하면 다른 이유의 409 에도
 * 갱신 CTA 가 붙는다.
 */
function readBlocked(envelope: ErrorEnvelope | undefined): BlockedDetail | undefined {
  const blockReason = readOptionalString(envelope?.blockReason)
  if (!blockReason) return undefined

  return {
    blockReason,
    feature: readOptionalString(envelope?.feature) ?? null,
  }
}

/**
 * 400 필드별 검증 실패 봉투를 읽는다(#594 §2).
 * 🚨 판별 기준은 **`fieldErrors` 가 비어 있지 않은 배열인지**다. 형식이 안 맞는 항목은 조용히
 * 건너뛴다(모르는 필드까지 버리면 폼이 오류를 하나도 못 보여준다 — `message` 는 항상 살린다).
 */
function readFieldErrors(envelope: ErrorEnvelope | undefined): VenueProfileFieldError[] | undefined {
  if (!Array.isArray(envelope?.fieldErrors) || envelope.fieldErrors.length === 0) return undefined

  const entries = (envelope.fieldErrors as ErrorFieldEntry[])
    .map((entry) => {
      const field = readOptionalString(entry.field)
      const message = readOptionalString(entry.message)
      const code = readOptionalString(entry.code) ?? 'UNKNOWN_VALUE'
      if (!field || !message) return null
      return { field, code, message }
    })
    .filter((entry): entry is VenueProfileFieldError => entry !== null)

  return entries.length > 0 ? entries : undefined
}

/**
 * 예약 충돌 409 전용 봉투를 읽는다(#604).
 * 🚨 판별 기준은 **`conflictReservationCount` 가 숫자인지**다 — 게이팅 409 는 이 필드가 아예 없다.
 */
function readConflict(envelope: ErrorEnvelope | undefined): ConflictDetail | undefined {
  if (typeof envelope?.conflictReservationCount !== 'number') return undefined

  return {
    count: envelope.conflictReservationCount,
    confirmedCount: typeof envelope.confirmedCount === 'number' ? envelope.confirmedCount : 0,
    pendingCount: typeof envelope.pendingCount === 'number' ? envelope.pendingCount : 0,
    horizonDays: typeof envelope.conflictHorizonDays === 'number' ? envelope.conflictHorizonDays : 0,
  }
}

/**
 * 예약 변경 슬롯 충돌 409 전용 봉투를 읽는다(#669 §3-1).
 * 🚨 판별 기준은 **`conflictReason` 이 문자열로 존재하는지**다 — 사유 코드 없는 409·이용권 차단 409는
 * 이 필드가 없어 자연히 걸러진다.
 */
function readRescheduleConflict(envelope: ErrorEnvelope | undefined): RescheduleConflictDetail | undefined {
  const conflictReason = readOptionalString(envelope?.conflictReason)
  if (!conflictReason) return undefined

  return {
    conflictReason,
    conflictReasonLabel: readOptionalString(envelope?.conflictReasonLabel) ?? conflictReason,
    retriable: envelope?.retriable === true,
    blockedSlotStartsAt: readOptionalString(envelope?.blockedSlotStartsAt) ?? null,
  }
}

/**
 * 예약 기능 활성화 조건 미충족 409 전용 봉투를 읽는다(#758, 백엔드 #757 §2).
 * 🚨 판별 기준은 **`unmetConditions` 가 비어 있지 않은 배열인지**다. 형식이 안 맞는 항목은 조용히
 * 건너뛴다(`readFieldErrors` 와 같은 관례) — `condition`·`label`·`description` 이 모두 문자열인
 * 항목만 담는다.
 */
function readReservationUnmetConditions(
  envelope: ErrorEnvelope | undefined
): VenueReservationUnmetCondition[] | undefined {
  if (!Array.isArray(envelope?.unmetConditions) || envelope.unmetConditions.length === 0) return undefined

  const entries = (envelope.unmetConditions as { condition?: unknown; label?: unknown; description?: unknown }[])
    .map((entry) => {
      const condition = readOptionalString(entry.condition)
      const label = readOptionalString(entry.label)
      const description = readOptionalString(entry.description)
      if (!condition || !label || !description) return null
      return { condition, label, description }
    })
    .filter((entry): entry is VenueReservationUnmetCondition => entry !== null)

  return entries.length > 0 ? entries : undefined
}

/** 어떤 예외든 화면이 쓸 수 있는 `ApiError` 로 정규화한다. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error

  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 0
    const body = error.response?.data as ErrorEnvelope | string | undefined
    const envelope = typeof body === 'object' && body !== null ? body : undefined
    const message = envelope?.errorMsg?.trim() || fallbackMessage(status)
    return new ApiError(status, envelope?.errorCode ?? status, message, {
      reason: readOptionalString(envelope?.reason),
      blocked: readBlocked(envelope),
      fieldErrors: readFieldErrors(envelope),
      conflict: readConflict(envelope),
      rescheduleConflict: readRescheduleConflict(envelope),
      reservationUnmetConditions: readReservationUnmetConditions(envelope),
    })
  }

  if (error instanceof Error) return new ApiError(0, 0, error.message || DEFAULT_MESSAGE)
  return new ApiError(0, 0, DEFAULT_MESSAGE)
}

function fallbackMessage(status: number): string {
  switch (status) {
    case 0:
      return '서버에 연결하지 못했습니다. 네트워크 상태를 확인해 주세요.'
    case 401:
      return '로그인이 필요합니다.'
    case 403:
      return '해당 구장의 관리자가 아닙니다.'
    case 404:
      return '구장을 찾을 수 없습니다.'
    case 409:
      return '지금은 사용할 수 없는 기능입니다.'
    default:
      return DEFAULT_MESSAGE
  }
}
